import cuid from "cuid";
import { get, isNil } from "lodash";

export const SCREEN_VERSION_STATUS = {
  DRAFT: "draft",
  PUBLISHED: "published",
};

export const DEFAULT_SOURCE_LOCALE = "fr-FR";

export const normalizeSlots = (slots) => (Array.isArray(slots) ? slots : []);

export const resolveSlotsForDraft = (versionSlots, states = []) => {
  const normalized = normalizeSlots(versionSlots);
  if (normalized.length) return normalized;
  for (const state of states) {
    const stateSlots = normalizeSlots(
      get(state, "bindingManifest.slots") || state.slots,
    );
    if (stateSlots.length) return stateSlots;
  }
  return [];
};

export const buildStatesPayload = (states = []) =>
  states.map((state) => ({
    id: cuid(),
    name: state.name,
    order: isNil(state.order) ? 0 : state.order,
    htmlContent: state.htmlContent || "",
    conditions: state.conditions || [],
    bindingManifest: {
      id: cuid(),
      slots: normalizeSlots(get(state, "bindingManifest.slots") || state.slots),
    },
  }));

/**
 * Creates a screen (when needed), a draft version, and optionally runs slot
 * detection when the draft has no slots to copy.
 */
export const createScreenDraft = async ({
  addScreen,
  addScreenVersion,
  detectSlots,
  statusId,
  screenId,
  description,
  versionNumber,
  htmlContent,
  styleContent,
  slots = [],
  states = [],
  copyStates = false,
  translations = {},
  published = false,
  updateScreen,
  processId = null,
  draftOnly = false,
}) => {
  let nextScreenId = screenId;
  if (!nextScreenId) {
    nextScreenId = cuid();
    await addScreen({
      variables: {
        data: {
          id: nextScreenId,
          ...(draftOnly
            ? { process: { id: processId }, draftStatusId: statusId }
            : { status: { id: statusId } }),
          description,
        },
      },
    });
  }

  const nextVersionNumber = versionNumber ? Number(versionNumber) + 1 : 1;
  const versionId = cuid();
  const draftSlots = resolveSlotsForDraft(slots, states);

  await addScreenVersion({
    variables: {
      data: {
        id: versionId,
        screen: { id: nextScreenId },
        versionNumber: nextVersionNumber,
        status: published
          ? SCREEN_VERSION_STATUS.PUBLISHED
          : SCREEN_VERSION_STATUS.DRAFT,
        htmlContent,
        styleContent,
        sourceLocale: DEFAULT_SOURCE_LOCALE,
        bindingManifest: {
          id: cuid(),
          slots: draftSlots,
        },
        translationTable: {
          id: cuid(),
          entries: translations || {},
        },
        ...(copyStates ? { states: buildStatesPayload(states) } : {}),
      },
    },
  });

  if (!draftSlots.length && detectSlots) {
    await detectSlots({
      variables: { screenVersion: { id: versionId } },
    });
  }

  if (published && updateScreen) {
    try {
      await updateScreen({
        variables: {
          where: { id: nextScreenId },
          data: { currentPublishedVersion: { id: versionId } },
        },
      });
    } catch (_error) {
      // Version is already stored as published; the pointer is best-effort.
    }
  }

  return { screenId: nextScreenId, versionId };
};

export const DRAFT_STATE_NOT_FOUND = "DRAFT_STATE_NOT_FOUND";

export const draftCopyOf = (copies = [], source = {}) => {
  const sameOrder = copies.filter((state) => state.order === source.order);
  const sameName = sameOrder.filter((state) => state.name === source.name);
  if (sameName.length === 1) return sameName[0];
  if (sameName.length === 0 && sameOrder.length === 1) return sameOrder[0];
  return null;
};

export const draftStateIdOf =
  (states = [], copies = []) =>
  (id) => {
    const source = states.find((state) => state.id === id);
    if (isNil(source)) return id;
    const copy = draftCopyOf(copies, source);
    if (isNil(copy)) throw new Error(DRAFT_STATE_NOT_FOUND);
    return copy.id;
  };

export const ensureDraftTarget = async ({
  ensureScreenDraft,
  screen,
  version,
  states = [],
}) => {
  if (get(version, "status") !== SCREEN_VERSION_STATUS.PUBLISHED)
    return { version, stateIdOf: (id) => id };
  const result = await ensureScreenDraft({
    variables: { where: { id: screen.id } },
  });
  const draft = get(result, "data.ensureScreenDraft");
  if (isNil(draft))
    throw new Error(
      get(result, "errors.graphQLErrors.0.message") ||
        get(result, "errors.message") ||
        "SCREEN_DRAFT_FAILED",
    );
  return {
    version: draft,
    stateIdOf: draftStateIdOf(states, draft.states || []),
  };
};
